// index.ts
import type { TicketSnapConfig, Snapshot, RedactRule, RouteInfo, TriggerMode, BreadcrumbEntry, TicketUser, CreateTicketOptions, NotificationsOptions, TicketCategoryDef, LinkableResourceDef, SnapMode, LauncherConfig, ResourceLink, LinkResolveInfo } from './types';
import { openPopup, toast, openTicketForm, mountLauncher, type LauncherHandle } from './ui';
import { getMessages, resolveLocale, resolveDir, type SnapMessages } from './i18n';

/** Injected by tsup at build time from package.json — see tsup.config.ts. */
declare const __VERSION__: string;

let CFG: (Required<TicketSnapConfig> & { triggerMode: TriggerMode });
const RING: any[] = [];                         // recent network
const LOGS: any[] = [];                         // recent console
const BREADCRUMBS: BreadcrumbEntry[] = [];     // user/app trail
let CONTEXT: Record<string, unknown> = {};     // mutable context per snapshot
let ACTIVE_LINK: ResourceLink | null = null;   // resource the user is currently on (e.g. an Order)
let ORIGINAL_FETCH: typeof fetch | null = null;
let XHR_OPEN: any, XHR_SEND: any;

const INTERNAL_FLAG = 'x-ticket-snap';          // re-entrancy guard

const DEFAULT_REDACT: RedactRule[] = [
  { key: /authorization/i, replaceWith: '***' },
  { key: /cookie/i, replaceWith: '***' },
  { pattern: /"password"\s*:\s*".+?"/gi, replaceWith: '"password":"***"' },
  { pattern: /\bBearer\s+[A-Za-z0-9._-]+/g, replaceWith: 'Bearer ***' },
  { pattern: /(\+964\d{9,12})/g, replaceWith: '+964*********' },
  { pattern: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, replaceWith: '***@***' }
];

// 1s delay helper (before sending)
function sleep(ms: number) { return new Promise(res => setTimeout(res, ms)); }

let NOTIF_TIMER: ReturnType<typeof setInterval> | null = null;
let LAUNCHER: LauncherHandle | null = null;

// Built-in i18n: resolved once from the launcher config (or navigator.language). English fallback.
let MSG: SnapMessages = getMessages('en');
let DIR: 'ltr' | 'rtl' = 'ltr';

// Base URL of the public API, derived from the bug-reports endpoint.
function deriveApiBase(endpoint: string): string {
  let e = (endpoint || '').trim().replace(/\/+$/, '');
  if (/\/bug-reports$/i.test(e)) e = e.replace(/\/bug-reports$/i, '');
  return e;
}

function init(user: TicketSnapConfig) {
  const inferredMode: TriggerMode = user.triggerMode ?? (user.autoSubmitOn500 ? 'auto' : 'prompt');
  const apiBase = deriveApiBase(user.endpoint);

  CFG = {
    endpoint: user.endpoint,
    projectKey: user.projectKey ?? 'TICKET-WEB',
    labels: user.labels ?? ['auto-captured','source:web'],
    promptUser: user.promptUser ?? true,
    autoSubmitOn500: user.autoSubmitOn500 ?? false, // kept for back-compat
    includeScreenshot: user.includeScreenshot ?? true,
    screenshotStrategy: user.screenshotStrategy ?? 'auto',
    auth: user.auth ?? { mode: 'none' },
    transport: user.transport ?? ((user.auth?.mode === 'basic') ? 'formFields' : 'metadataPart'),
    redact: [...DEFAULT_REDACT, ...(user.redact ?? [])],
    ringSize: user.ringSize ?? 20,
    networkCaptureMode: user.networkCaptureMode ?? 'all',
    networkRecentCount: user.networkRecentCount ?? 10,
    maxBodyChars: user.maxBodyChars ?? 2000,
    maxConsole: user.maxConsole ?? 100,
    maxPerf: user.maxPerf ?? 150,
    routeResolver: user.routeResolver ?? (() => ({ route: location.pathname, params: {} })),
    resolveLink: user.resolveLink ?? (() => ACTIVE_LINK),
    app: user.app ?? {},
    onSubmitted: user.onSubmitted ?? (()=>{}),
    onError: user.onError ?? (()=>{}),
    triggerMode: inferredMode,
    maxBreadcrumbs: user.maxBreadcrumbs ?? 30,
    captureNavigationBreadcrumbs: user.captureNavigationBreadcrumbs ?? true,
    initialContext: user.initialContext ?? {},
    user: user.user ?? { phone: '' },
    ticketsEndpoint: user.ticketsEndpoint ?? `${apiBase}/tickets`,
    ticketTypesEndpoint: user.ticketTypesEndpoint ?? `${apiBase}/ticket-types`,
    notificationsEndpoint: user.notificationsEndpoint ?? `${apiBase}/notifications`,
    linkableResourcesEndpoint: user.linkableResourcesEndpoint ?? `${apiBase}/linkable-resources`,
    launcher: user.launcher ?? {}
  };

  CONTEXT = { ...(user.initialContext ?? {}) };

  // Resolve the built-in language + direction once (config wins, else navigator.language / host dir).
  const locale = resolveLocale(CFG.launcher?.locale);
  MSG = getMessages(locale);
  DIR = resolveDir(CFG.launcher?.dir, locale);

  if (!CFG.endpoint) console.warn('[ticket-snap] endpoint is required');

  installConsoleCapture();
  installFetch();
  installXHR();
  installGlobalErrors();
  if (CFG.captureNavigationBreadcrumbs) installNavigationBreadcrumbs();

  // Only auto-flush when not in manual mode
  if (CFG.triggerMode !== 'manual') {
    setTimeout(() => { void flushOffline(); }, 1200);
  }

  mountLauncherIfEnabled();
  return api;
}

// ---------- launcher ----------
function mountLauncherIfEnabled() {
  if (typeof document === 'undefined') return;
  const lc = CFG.launcher ?? {};
  const modes: SnapMode[] = (lc.modes ?? ['error', 'support']).filter(m => m === 'error' || m === 'support');
  const enabled = lc.enabled ?? modes.length > 0;
  if (!enabled || modes.length === 0) return;

  const mount = () => {
    LAUNCHER?.destroy();
    LAUNCHER = mountLauncher({
      modes,
      position: lc.position ?? 'bottom-right',
      dir: DIR,
      accent: lc.themeColor,
      theme: lc.theme,
      colorPreset: lc.colorPreset ?? 'aurora',
      palette: lc.palette,
      title: lc.title ?? MSG.launcher.title,
      subtitle: lc.subtitle ?? MSG.launcher.subtitle,
      error: {
        label: lc.errorLabel ?? MSG.launcher.errorLabel,
        description: lc.errorDescription ?? MSG.launcher.errorDescription
      },
      support: {
        label: lc.supportLabel ?? MSG.launcher.supportLabel,
        description: lc.supportDescription ?? MSG.launcher.supportDescription
      },
      onError: () => api.reportNow(),
      onSupport: () => { void openTicketFormFlow(CFG.launcher?.support ?? {}); }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
}

/** Pluck theming hints from the launcher config so every surface stays in sync. */
function themingArgs() {
  const lc = CFG.launcher ?? {};
  return {
    colorPreset: lc.colorPreset ?? 'aurora' as const,
    theme: lc.theme,
    accent: lc.themeColor,
    palette: lc.palette,
    // Built-in i18n threaded into every dialog/toast (English fallback resolved at init()).
    messages: MSG,
    dir: DIR
  };
}

// ---------- helpers
function headersToObject(h: HeadersInit | undefined): Record<string,string> {
  const out: Record<string,string> = {}; if (!h) return out;
  if (Array.isArray(h)) { h.forEach(([k,v]) => { out[k]=String(v); }); return out; }
  if ((h as any).forEach) (h as any).forEach((v: any, k: string) => { out[k]=String(v); });
  else Object.entries(h as any).forEach(([k,v]) => out[k]=String(v));
  return out;
}
function hasHeader(h: HeadersInit | undefined, name: string): boolean {
  if (!h) return false;
  const needle = name.toLowerCase();
  if (Array.isArray(h)) return h.some(([k]) => k.toLowerCase() === needle);
  if ((h as any).get) return (h as any).get(name) !== null; // FIX: real null check
  const obj = h as Record<string,string>;
  return Object.keys(obj).some(k => k.toLowerCase() === needle);
}
function redactHeaders(h: Record<string,string>): Record<string,string> {
  const o: Record<string,string> = {};
  Object.keys(h).forEach(k => {
    const m = CFG.redact.find(r => 'key' in r && (r as any).key.test(k)) as {key:RegExp, replaceWith?: string}|undefined;
    o[k] = m ? (m.replaceWith ?? '***') : h[k];
  });
  return o;
}
function redactText(text?: string): string | undefined {
  if (!text) return text;
  let out = text; CFG.redact.forEach(r => { if ('pattern' in r) out = out.replace((r as any).pattern, (r as any).replaceWith); });
  return out;
}
function clamp(s: string | undefined, n: number): {text?: string; truncated: boolean} {
  if (!s) return { text: s, truncated: false };
  if (s.length > n) return { text: s.slice(0, n) + '…[truncated]', truncated: true };
  return { text: s, truncated: false };
}
function serializeBody(body: any, limit = CFG.maxBodyChars): {text?: string; truncated: boolean} {
  if (!body) return { text: undefined, truncated: false };
  if (typeof body === 'string') return clamp(redactText(body), limit);
  if (body instanceof FormData) {
    const o: Record<string,any> = {};
    body.forEach((v,k) => { o[k] = (v as any)?.name ? `[File ${(v as any).name}]` : v; });
    return clamp(redactText(JSON.stringify(o)), limit);
  }
  try { return clamp(redactText(JSON.stringify(body)), limit); } catch { return { text: '[unserializable]', truncated: false }; }
}

/**
 * Flatten a link to the redacted flat wire fields, shared by the live bug-report submit, the typed
 * createTicket multipart path, and the offline replay. `rawJson` and the `fields` rows are host-supplied
 * free-form data, so they pass through `redactText` (the same scrub applied to captured bodies) — they
 * are NOT exempt from `CFG.redact` just because the host set them. Field names match the backend's flat
 * intake (`LinkFieldsJson`, not `LinkFields`).
 */
function linkFormFields(link: ResourceLink | null | undefined): Record<string,string> | null {
  if (!link?.type || !link?.id) return null;
  const o: Record<string,string> = { LinkType: link.type, LinkForeignId: link.id };
  if (link.label) o.LinkLabel = link.label;
  if (link.status) o.LinkStatus = link.status;
  if (link.fields?.length) o.LinkFieldsJson = redactText(JSON.stringify(link.fields))!;
  if (link.rawJson) o.LinkRawJson = redactText(link.rawJson)!;
  return o;
}
function appendFormFields(fd: FormData, fields: Record<string,string> | null | undefined): void {
  if (fields) for (const [k, v] of Object.entries(fields)) fd.append(k, v);
}

function pushRing(arr: any[], max: number, val: any) { arr.push(val); while (arr.length > max) arr.shift(); }

/** True when entry is an error: no response (network failure), status 0, or status >= 400 */
function isErrorEntry(entry: any): boolean {
  if (!entry?.response) return true; // network error
  const status = entry.response?.status;
  return status === 0 || (typeof status === 'number' && status >= 400);
}

/** Whether to add this request to the ring based on networkCaptureMode */
function shouldPushToRing(entry: any): boolean {
  if (CFG.networkCaptureMode === 'errorsOnly') return isErrorEntry(entry);
  return true;
}

function pageMeta() {
  const reducedMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  return {
    url: location.href,
    title: document.title,
    referrer: document.referrer || '',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    ua: navigator.userAgent,
    capturedAt: new Date().toISOString(),
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    scrollHeight: document.documentElement?.scrollHeight ?? 0,
    scrollWidth: document.documentElement?.scrollWidth ?? 0,
    reducedMotion
  };
}

async function captureScreenshotBlob(): Promise<Blob|null> {
  if (!CFG.includeScreenshot || CFG.screenshotStrategy === 'none') return null;
  // prefer html2canvas if present
  // @ts-ignore
  const h2c = (window as any).html2canvas;
  if ((CFG.screenshotStrategy === 'html2canvas' || CFG.screenshotStrategy === 'auto') && typeof h2c === 'function') {
    try { const canvas = await h2c(document.body, { useCORS: true, allowTaint: false });
      return await new Promise<Blob|null>(res => canvas.toBlob((b: Blob | PromiseLike<Blob | null> | null) => res(b), 'image/png')); } catch {}
  }
  if (CFG.screenshotStrategy === 'svg-foreignObject' || CFG.screenshotStrategy === 'auto') {
    try {
      const w = Math.max(document.documentElement.scrollWidth, document.body.scrollWidth);
      const h = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
      const xhtml = new XMLSerializer().serializeToString(document.documentElement);
      const data = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><foreignObject width="100%" height="100%">${xhtml}</foreignObject></svg>`;
      const img = new Image();
      await new Promise<void>((ok,err) => { img.onload = () => ok(); img.onerror = () => err(); img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(data); });
      const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d')!; ctx.drawImage(img, 0, 0);
      return await new Promise<Blob|null>(res => canvas.toBlob(b => res(b), 'image/png'));
    } catch {}
  }
  return null;
}

/**
 * Pixel-perfect, on-demand capture via the Screen Capture API. Must be called from a user
 * gesture (e.g. the popup's "Capture screen" button) and needs a secure context (https/localhost).
 * The video→canvas path is primary because ImageCapture.grabFrame() is Chromium-only.
 */
async function captureViaDisplayMedia(): Promise<Blob|null> {
  const md: any = (navigator as any).mediaDevices;
  if (!md?.getDisplayMedia) return null;
  let stream: MediaStream | null = null;
  try {
    stream = await md.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true                 // Chromium hint: pre-select the current tab
    });
    const track = stream!.getVideoTracks()[0];
    if (!track) return null;

    const video = document.createElement('video');
    video.muted = true; (video as any).playsInline = true; video.srcObject = stream;
    await new Promise<void>((ok, err) => {
      video.onloadeddata = () => ok();
      video.onerror = () => err(new Error('video load failed'));
      video.play().catch(err);
    });
    // One extra frame so the first painted frame is real pixels, not black.
    await new Promise<void>(r => requestAnimationFrame(() => r()));

    const w = video.videoWidth, h = video.videoHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    canvas.getContext('2d')!.drawImage(video, 0, 0, w, h);
    video.pause(); video.srcObject = null;
    return await new Promise<Blob|null>(res => canvas.toBlob(b => res(b), 'image/png'));
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach(t => t.stop());
  }
}

/**
 * Open the "Report a problem" dialog for a snapshot and wire up submit + on-demand screen capture.
 * Centralises what every error path (fetch/xhr/global/reportNow) used to duplicate.
 */
function presentReport(snap: Snapshot, prefixNotes = '') {
  const jsonText = JSON.stringify({
    page: snap.page, request: snap.request, response: snap.response,
    env: snap.env, error: snap.error, link: snap.link ?? undefined,
    breadcrumbs: snap.breadcrumbs, context: snap.context
  }, null, 2);
  const ssUrl = snap.page.screenshot ? URL.createObjectURL(snap.page.screenshot) : null;
  openPopup(jsonText, {
    screenshotUrl: ssUrl,
    link: snap.link ? { type: snap.link.type, id: snap.link.id, label: snap.link.label, status: snap.link.status } : null,
    onCaptureScreen: () => captureViaDisplayMedia(),
    ...themingArgs(),
    onSubmit: (notes, shot) => {
      if (shot) snap.page.screenshot = shot;        // user retook a pixel-perfect shot
      const merged = prefixNotes ? (notes ? `${prefixNotes}\n${notes}` : prefixNotes) : notes;
      void submitSnapshot(snap, merged);
    },
    onCancel: () => {}
  });
}

/** Either auto-submit (auto mode) or open the report dialog (prompt mode); manual is a no-op. */
async function handleSnapshot(snap: Snapshot) {
  const mode = CFG.triggerMode;
  if (mode === 'auto') await submitSnapshot(snap, '');
  else if (mode === 'prompt') presentReport(snap);
}

function addBreadcrumbInternal(message: string, category?: string) {
  pushRing(BREADCRUMBS, CFG.maxBreadcrumbs, { at: new Date().toISOString(), category, message });
}

function buildSnapshot(entry?: any, errorInfo?: {message:string;stack?:string;source?:string}): Snapshot {
  const pg = pageMeta();
  const env = { locale: navigator.language || 'en', tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Baghdad', ua: pg.ua };
  const snap: Snapshot = {
    page: pg,
    request: entry?.request ?? null,
    response: entry?.response ?? null,
    error: errorInfo ?? null,
    env,
    networkRecent: RING.slice(-CFG.networkRecentCount),
    breadcrumbs: BREADCRUMBS.length ? BREADCRUMBS.slice(-CFG.maxBreadcrumbs) : undefined,
    context: Object.keys(CONTEXT).length ? { ...CONTEXT } : undefined
  };
  snap.link = resolveSnapshotLink(snap);
  return snap;
}

/** Keep only a well-formed link (needs both a type and an id). */
function normalizeLink(link: ResourceLink | null | undefined): ResourceLink | null {
  return link && link.type && link.id ? link : null;
}

/**
 * Resolve the cross-system resource this error is about — host `resolveLink()` first (which
 * defaults to the link set via `setActiveLink()`). Never throws: falls back to the active link.
 */
function resolveSnapshotLink(snap: Snapshot): ResourceLink | null {
  try {
    const route: RouteInfo = (CFG.routeResolver?.() || {});
    const info: LinkResolveInfo = { page: snap.page, request: snap.request, response: snap.response, route };
    return normalizeLink(CFG.resolveLink?.(info));
  } catch {
    return normalizeLink(ACTIVE_LINK);
  }
}

function isTicketUrl(url: string): boolean {
  try {
    const target = new URL(CFG.endpoint, location.href);
    const u = new URL(url, location.href);
    return target.origin === u.origin && u.pathname.startsWith(target.pathname);
  } catch { return false; }
}

function asHeaderArrayJson(map: Record<string, string>): string {
  const obj: Record<string, string[]> = {};
  Object.entries(map).forEach(([k,v]) => { obj[k] = [String(v)]; });
  return JSON.stringify(obj);
}

async function sha256Hex(str: string): Promise<string> {
  try {
    const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
    return [...new Uint8Array(d)].map(b=>b.toString(16).padStart(2,'0')).join('');
  } catch {
    // fallback (non-crypto) – not cryptographic, but stable
    let h = 0; for (let i=0;i<str.length;i++) { h = (h<<5)-h + str.charCodeAt(i); h |= 0; }
    return ('00000000'+(h>>>0).toString(16)).slice(-8);
  }
}

// ---------- auth + query
function applyAuth(url: string, headers: Headers) {
  if (CFG.auth?.mode === 'ingestKey') {
    try { const u = new URL(url, location.href); u.searchParams.set('ingestKey', (CFG.auth as any).key); url = u.toString(); } catch {}
  }
  if (CFG.auth?.mode === 'basic') {
    const token = btoa(`${CFG.projectKey}:${(CFG.auth as any).secret}`);
    headers.set('Authorization', `Basic ${token}`);
  }
  headers.set(INTERNAL_FLAG, '1'); // mark as internal to avoid self-capture
  return url;
}

// ---------- typed cross-system tickets ----------
function authHeaders(): Headers {
  const h = new Headers();
  h.set('Content-Type', 'application/json');
  if (CFG.auth?.mode === 'basic') h.set('Authorization', `Basic ${btoa(`${CFG.projectKey}:${(CFG.auth as any).secret}`)}`);
  h.set(INTERNAL_FLAG, '1');
  return h;
}

/** Fetch the project's configured categories/subtypes for rendering a picker. */
async function fetchTicketTypes(): Promise<TicketCategoryDef[]> {
  const url = `${CFG.ticketTypesEndpoint}?projectKey=${encodeURIComponent(CFG.projectKey)}`;
  const r = await fetch(url, { method: 'GET', headers: authHeaders() });
  const json: any = await r.json().catch(() => ({}));
  return (json?.data ?? json?.result ?? []) as TicketCategoryDef[];
}

/** Fetch the project's configured linkable resource types (Order, Inspection, ...). */
async function fetchLinkableResources(): Promise<LinkableResourceDef[]> {
  const url = `${CFG.linkableResourcesEndpoint}?projectKey=${encodeURIComponent(CFG.projectKey)}`;
  const r = await fetch(url, { method: 'GET', headers: authHeaders() });
  const json: any = await r.json().catch(() => ({}));
  return (json?.data ?? json?.result ?? []) as LinkableResourceDef[];
}

/** Auth headers for multipart requests — same as authHeaders() but without Content-Type. */
function authHeadersMultipart(): Headers {
  const h = new Headers();
  if (CFG.auth?.mode === 'basic') h.set('Authorization', `Basic ${btoa(`${CFG.projectKey}:${(CFG.auth as any).secret}`)}`);
  h.set(INTERNAL_FLAG, '1');
  return h;
}

/** Create a typed ticket directly (no UI). Returns the created ticket number, or null on failure. */
async function createTicket(opts: CreateTicketOptions): Promise<string | null> {
  const u: TicketUser = CFG.user || { phone: '' };
  if (!u.phone) { console.warn('[ticket-snap] user.phone is required to create a ticket'); }
  const tArgs = themingArgs();
  const hasAttachments = !!opts.attachments?.length;

  try {
    let r: Response;
    if (hasAttachments) {
      // Multipart path — sends fields + image blobs together so the backend can attach them.
      const fd = new FormData();
      fd.append('ProjectKey', CFG.projectKey);
      fd.append('Title', opts.title ?? 'Issue');
      if (opts.description) fd.append('Description', opts.description);
      fd.append('Priority', String(opts.priority ?? 3));
      if (opts.category) fd.append('CategoryCode', opts.category);
      if (opts.subtype) fd.append('SubtypeCode', opts.subtype);
      fd.append('RequesterName', u.name ?? u.handle ?? u.phone);
      fd.append('RequesterPhoneNumber', u.phone);
      if (u.externalSystem) fd.append('ExternalSystem', u.externalSystem);
      if (u.externalUserId) fd.append('ExternalUserId', u.externalUserId);
      if (u.handle) fd.append('ExternalHandle', u.handle);
      // Flat link fields, redacted, with backend-matching names (LinkFieldsJson). The typed /public/tickets
      // multipart action mirrors the bug-report shape, so the same flattening applies.
      appendFormFields(fd, linkFormFields(opts.link));
      opts.attachments!.forEach((blob, i) => {
        const name = (blob as any).name || `attachment-${Date.now()}-${i}.png`;
        fd.append('Attachments', blob, name);
      });
      // Separate route from the JSON action (avoids an ambiguous path+verb for OpenAPI).
      r = await fetch(`${CFG.ticketsEndpoint}/upload`, { method: 'POST', headers: authHeadersMultipart(), body: fd });
    } else {
      const payload = {
        projectKey: CFG.projectKey,
        title: opts.title ?? 'Issue',
        description: opts.description,
        priority: opts.priority ?? 3,
        categoryCode: opts.category,
        subtypeCode: opts.subtype,
        requesterName: u.name ?? u.handle ?? u.phone,
        requesterPhoneNumber: u.phone,
        externalSystem: u.externalSystem,
        externalUserId: u.externalUserId,
        externalHandle: u.handle,
        link: opts.link ? {
          linkType: opts.link.type,
          foreignId: opts.link.id,
          label: opts.link.label,
          status: opts.link.status,
          fields: opts.link.fields?.map(f => ({ ...f, value: redactText(f.value) })),
          rawJson: redactText(opts.link.rawJson)
        } : undefined
      };
      r = await fetch(CFG.ticketsEndpoint, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
    }
    const json: any = await r.json().catch(() => ({}));
    const number = json?.data?.ticketNumber ?? json?.result?.ticketNumber ?? null;
    if (r.ok && number) { CFG.onSubmitted(json); toast(MSG.toast.submitted, tArgs); return number; }
    CFG.onError(json); toast(MSG.toast.couldNotSubmit, tArgs); return null;
  } catch (e) {
    CFG.onError(e); toast(MSG.toast.networkError, tArgs); return null;
  }
}

/** Open the typed creation form (fetches the project's categories), then submit on confirm. */
async function openTicketFormFlow(opts: CreateTicketOptions = {}) {
  let categories: TicketCategoryDef[] = [];
  try { categories = await fetchTicketTypes(); } catch { categories = []; }

  // "Get support" is its OWN flow (solve an issue), distinct from "Report a problem".
  // Always open the support modal — when the project has no configured ticket types it
  // shows an empty state instead of falling back to the bug-report popup. Order categories
  // and subtypes by the backend's sortOrder (then label) so they appear in the intended order.
  const byOrder = <T extends { sortOrder?: number; label: string }>(a: T, b: T) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label);
  const ordered = categories.slice().sort(byOrder).map(c => ({
    code: c.code, label: c.label,
    subtypes: (c.subtypes ?? []).slice().sort(byOrder).map(s => ({ code: s.code, label: s.label }))
  }));

  const tArgs = themingArgs();
  openTicketForm({
    categories: ordered,
    prefill: { category: opts.category, subtype: opts.subtype, title: opts.title, description: opts.description, linkLabel: opts.link?.label ?? opts.link?.id },
    allowAttachments: CFG.launcher?.supportAttachments !== false,
    onCaptureScreen: () => captureViaDisplayMedia(),
    onSubmit: (data) => { void createTicket({
      ...opts,
      category: data.category, subtype: data.subtype, title: data.title, description: data.description,
      attachments: data.attachments
    }); },
    onCancel: () => {},
    ...tArgs
  });
}

// ---------- completion notifications (notify-back) ----------
async function pollNotificationsOnce() {
  const phone = CFG.user?.phone;
  if (!phone) return;
  try {
    const url = `${CFG.notificationsEndpoint}?projectKey=${encodeURIComponent(CFG.projectKey)}&phone=${encodeURIComponent(phone)}`;
    const r = await fetch(url, { method: 'GET', headers: authHeaders() });
    const json: any = await r.json().catch(() => ({}));
    const items: any[] = json?.data ?? json?.result ?? [];
    for (const n of items) {
      toast(n.body || n.title || 'Ticket updated', themingArgs());
      try {
        await fetch(`${CFG.notificationsEndpoint}/${encodeURIComponent(n.id)}/seen`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ projectKey: CFG.projectKey, phone })
        });
      } catch {}
    }
  } catch {}
}

function enableNotifications(opts: NotificationsOptions = {}) {
  disableNotifications();
  const interval = opts.intervalMs ?? 60000;
  void pollNotificationsOnce();
  NOTIF_TIMER = setInterval(() => { void pollNotificationsOnce(); }, interval);
}

function disableNotifications() {
  if (NOTIF_TIMER) { clearInterval(NOTIF_TIMER); NOTIF_TIMER = null; }
}

// ---------- breadcrumbs
function installNavigationBreadcrumbs() {
  const push = () => addBreadcrumbInternal(`navigation: ${location.pathname || '/'}${location.search || ''}${location.hash || ''}`, 'navigation');
  window.addEventListener('popstate', push);
  window.addEventListener('hashchange', push);
  // Initial page
  addBreadcrumbInternal(`load: ${location.href}`, 'navigation');
}

// ---------- console/perf capture
function installConsoleCapture() {
  const levels: Array<keyof Console> = ['log','info','warn','error'];
  levels.forEach(level => {
    const orig = console[level] as any;
    console[level] = function(...args: any[]) {
      try { pushRing(LOGS, CFG.maxConsole, { level, at: new Date().toISOString(), text: args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ') }); } catch {}
      return orig.apply(console, args);
    } as any;
  });
}

function collectPerfEntries() {
  try {
    const nav = performance.getEntriesByType('navigation') as PerformanceEntry[];
    const res = performance.getEntriesByType('resource') as PerformanceEntry[];
    const take = [...nav, ...res].slice(-CFG.maxPerf).map(e => ({
      name: (e as any).name, type: e.entryType, start: e.startTime, dur: e.duration, initiator: (e as any).initiatorType
    }));
    return JSON.stringify(take);
  } catch { return '[]'; }
}

// ---------- submit (formFields path = your contract)
async function submitSnapshot(s: Snapshot, extraNotes: string) {
  const route: RouteInfo = (CFG.routeResolver?.() || {});
  const title = inferTitle(s);
  const summary = `Auto-captured error on ${s.page.url}`;
  // Strip the screenshot blob; redact the host-supplied link rawJson so it isn't re-leaked unscrubbed
  // inside SnapshotJson (it's also sent, redacted, as the LinkRawJson field below).
  const snapForJson: any = { ...s, page: { ...s.page, screenshot: undefined } };
  if (snapForJson.link?.rawJson) snapForJson.link = { ...snapForJson.link, rawJson: redactText(snapForJson.link.rawJson) };
  const snapJsonText = JSON.stringify(snapForJson);

  // Build request details
  const req = s.request;
  const res = s.response;

  const reqBody = req?.body || undefined;
  const resBody = res?.body || undefined;

  const reqHdrs = req?.headers || {};
  const resHdrs = res?.headers || {};

  const reqCt = reqHdrs['content-type'] || reqHdrs['Content-Type'] || undefined;
  const resCt = resHdrs['content-type'] || resHdrs['Content-Type'] || undefined;

  const reqSize = reqBody ? new TextEncoder().encode(reqBody).length : undefined;
  const resSize = resBody ? new TextEncoder().encode(resBody).length : undefined;

  const reqHash = reqBody ? await sha256Hex(reqBody) : undefined;
  const resHash = resBody ? await sha256Hex(resBody) : undefined;

  const fd = new FormData();

  // required
  fd.append('ProjectKey', CFG.projectKey);
  fd.append('Title', title);

  // optional simple
  if (summary) fd.append('Summary', summary);
  (CFG.labels || []).forEach(l => fd.append('Labels', l));
  fd.append('SnapshotJson', snapJsonText);
  if (extraNotes) fd.append('ExtraNotes', extraNotes);

  // routing
  if (route.method) fd.append('RoutingMethod', route.method);
  fd.append('RoutingUrl', route.urlOverride || s.page.url);
  if (route.route) fd.append('Route', route.route);
  if (route.params && Object.keys(route.params).length) fd.append('RouteParamsJson', JSON.stringify(route.params));

  // HTTP request
  if (req) {
    fd.append('RequestMethod', req.method);
    fd.append('RequestUrl', req.url);
    if (Object.keys(reqHdrs).length) fd.append('RequestHeadersJson', asHeaderArrayJson(reqHdrs));
    if (reqBody) fd.append('RequestBody', reqBody);
    if (reqCt) fd.append('RequestContentType', reqCt);
    if (reqSize !== undefined) fd.append('RequestSizeBytes', String(reqSize));
    if (reqHash) fd.append('RequestBodyHash', reqHash);
    fd.append('RequestTruncated', String(reqBody?.includes('…[truncated]') || false));
  }

  // HTTP response
  if (res) {
    if (res.status !== undefined) fd.append('ResponseStatusCode', String(res.status));
    if (Object.keys(resHdrs).length) fd.append('ResponseHeadersJson', asHeaderArrayJson(resHdrs));
    if (resBody) fd.append('ResponseBody', resBody);
    if (resCt) fd.append('ResponseContentType', resCt);
    if (resSize !== undefined) fd.append('ResponseSizeBytes', String(resSize));
    if (resHash) fd.append('ResponseBodyHash', resHash);
    fd.append('ResponseTruncated', String(resBody?.includes('…[truncated]') || false));
    if (res.durationMs !== undefined) fd.append('ResponseDurationMs', String(res.durationMs));
  }

  // Client/app/diag
  fd.append('UserAgent', navigator.userAgent);
  fd.append('Locale', navigator.language || 'en');
  fd.append('Referrer', document.referrer || '');
  fd.append('PageTitle', document.title || '');
  fd.append('Viewport', s.page.viewport);

  // App
  const app = CFG.app || {};
  if (app.appName) fd.append('AppName', app.appName);
  if (app.appVersion) fd.append('AppVersion', app.appVersion);
  if (app.environment) fd.append('Environment', app.environment);
  if (app.build) fd.append('Build', app.build);
  if (app.commit) fd.append('Commit', app.commit);
  if (app.releaseChannel) fd.append('ReleaseChannel', app.releaseChannel);

  // Console / Network / Perf
  if (LOGS.length) fd.append('ConsoleLogJson', JSON.stringify(LOGS.slice(-CFG.maxConsole)));
  const netForServer = RING.filter(e => !isTicketUrl(e?.request?.url || '') && !(e?.request?.headers?.[INTERNAL_FLAG]));
  if (netForServer.length) fd.append('NetworkLogJson', JSON.stringify(netForServer.slice(-CFG.networkRecentCount)));
  fd.append('PerfTimingsJson', collectPerfEntries());

  // Breadcrumbs & context (frontend trail and app state)
  if (BREADCRUMBS.length) fd.append('BreadcrumbsJson', JSON.stringify(BREADCRUMBS.slice(-CFG.maxBreadcrumbs)));
  if (Object.keys(CONTEXT).length) fd.append('ContextJson', JSON.stringify(CONTEXT));

  // DOM path (best-effort current focus)
  const focus = document.activeElement;
  if (focus) {
    const path: string[] = [];
    let el: any = focus;
    while (el && el.nodeType === 1 && path.length < 8) {
      const id = el.id ? `#${el.id}` : '';
      const cls = el.className && typeof el.className === 'string' ? '.'+el.className.trim().split(/\s+/).slice(0,2).join('.') : '';
      path.unshift(`${el.tagName.toLowerCase()}${id}${cls}`);
      el = el.parentElement;
    }
    fd.append('DomPath', path.join(' > '));
  }

  // Cross-system link — the resource this error is about (e.g. the Order being viewed).
  // Best-effort on the backend: an unconfigured type is ignored, never failing the report.
  // Redacted here, and reused verbatim for the offline replay so a queued report keeps its link.
  const linkFields = linkFormFields(s.link);
  appendFormFields(fd, linkFields);

  // screenshot as Attachment
  if (s.page.screenshot) fd.append('Attachments', s.page.screenshot, `screenshot-${Date.now()}.png`);

  const headers = new Headers();
  const url = applyAuth(CFG.endpoint, headers);

  try {
    // 1s delay before sending
    await sleep(1000);
    const r = await fetch(url, { method: 'POST', body: fd, headers });
    const json = await r.json().catch(()=>({}));
    CFG.onSubmitted(json);
    toast(MSG.toast.submitted, themingArgs());
  } catch (e) {
    CFG.onError(e);
    queueOffline({ title, summary, labels: CFG.labels, snapJsonText, extraNotes, route, app, transport: CFG.transport, linkFields }, s.page.screenshot || null);
    toast(MSG.toast.savedOffline, themingArgs());
  }
}

function inferTitle(s: Snapshot) {
  const st = s.response?.status ?? 'Error';
  const url = s.request?.url || s.page.url;
  return `${st} on ${url}`;
}

// ---------- fetch hook
function installFetch() {
  if (!window.fetch || ORIGINAL_FETCH) return;
  ORIGINAL_FETCH = window.fetch.bind(window);
  window.fetch = async (input: any, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : input?.url || '';
    const method = init?.method || (typeof input !== 'string' && input?.method) || 'GET';

    // skip our own / flagged requests
    if (isTicketUrl(url) || hasHeader(init?.headers, INTERNAL_FLAG)) {
      return ORIGINAL_FETCH!(input, init);
    }

    const reqHeaders = redactHeaders(headersToObject(init?.headers || (typeof input !== 'string' ? input?.headers : undefined)));
    const bodyS = serializeBody(init?.body);
    const t0 = performance.now();

    try {
      const res = await ORIGINAL_FETCH!(input, init);
      const clone = res.clone();
      const duration = performance.now() - t0;
      const resHeaders = redactHeaders(headersToObject(clone.headers));
      let text = ''; try { text = await clone.text(); } catch {}
      const bodyR = clamp(redactText(text), CFG.maxBodyChars);
      const entry = { request: { method, url, headers: reqHeaders, body: bodyS.text },
                      response: { status: res.status, headers: resHeaders, body: bodyR.text, durationMs: Math.round(duration) },
                      at: new Date().toISOString(), source: 'fetch',
                      requestTruncated: bodyS.truncated, responseTruncated: bodyR.truncated };
      if (shouldPushToRing(entry)) pushRing(RING, CFG.ringSize, entry);

      if (res.status >= 500) {
        addBreadcrumbInternal(`${entry.request.method} ${entry.request.url} → ${res.status}`, 'network');
        const shot = await captureScreenshotBlob();
        const snap = buildSnapshot(entry); snap.page.screenshot = shot;

        await handleSnapshot(snap);
      }
      return res;
    } catch (err:any) {
      if (isTicketUrl(url) || hasHeader(init?.headers, INTERNAL_FLAG)) throw err;

      const entry = { request: { method, url, headers: reqHeaders, body: bodyS.text },
                      response: null, at: new Date().toISOString(), source: 'fetch',
                      error: String(err?.message || err), requestTruncated: bodyS.truncated };
      if (shouldPushToRing(entry)) pushRing(RING, CFG.ringSize, entry);
      addBreadcrumbInternal(`${method} ${url} → network error`, 'network');

      const shot = await captureScreenshotBlob();
      const snap = buildSnapshot(entry, { message: String(err?.message || err), stack: err?.stack, source: 'fetch' }); snap.page.screenshot = shot;

      await handleSnapshot(snap);

      throw err;
    }
  };
}

// ---------- XHR hook
function installXHR() {
  if (!window.XMLHttpRequest) return;
  XHR_OPEN = XMLHttpRequest.prototype.open;
  XHR_SEND = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method: string, url: string) {
    (this as any).__tsnap = { method, url, t0: 0, reqHeaders: {}, body: '', skip: isTicketUrl(url) };
    return XHR_OPEN.apply(this, arguments as any);
  };

  const origSet = XMLHttpRequest.prototype.setRequestHeader;
  if (origSet) {
    XMLHttpRequest.prototype.setRequestHeader = function(k: string, v: string) {
      try { (this as any).__tsnap.reqHeaders[k] = v; if (k.toLowerCase() === INTERNAL_FLAG) (this as any).__tsnap.skip = true; } catch {}
      return origSet.apply(this, arguments as any);
    };
  }

  XMLHttpRequest.prototype.send = function(body?: any) {
    const self = this as any;
    if (self.__tsnap) { self.__tsnap.t0 = performance.now(); self.__tsnap.body = serializeBody(body); }

    this.addEventListener('loadend', async function() {
      try {
        if (self.__tsnap?.skip) return;

        const t1 = performance.now();
        const st = (this as XMLHttpRequest).status;
        const headersRaw = (this as XMLHttpRequest).getAllResponseHeaders() || '';
        const resHeaders: Record<string,string> = {};
        headersRaw.trim().split(/[\r\n]+/).forEach(line => { const i = line.indexOf(':'); if (i > -1) resHeaders[line.slice(0,i)] = line.slice(i+1).trim(); });

        const resText = (this as any).responseText as string | undefined;
        const bodyR = clamp(redactText(resText), CFG.maxBodyChars);

        const entry = { request: { method: self.__tsnap.method, url: self.__tsnap.url, headers: redactHeaders(self.__tsnap.reqHeaders), body: self.__tsnap.body.text },
                        response: { status: st, headers: redactHeaders(resHeaders), body: bodyR.text, durationMs: Math.round(t1 - self.__tsnap.t0) },
                        at: new Date().toISOString(), source: 'xhr',
                        requestTruncated: self.__tsnap.body.truncated, responseTruncated: bodyR.truncated };
        if (shouldPushToRing(entry)) pushRing(RING, CFG.ringSize, entry);

        if (st >= 500) {
          addBreadcrumbInternal(`${entry.request.method} ${entry.request.url} → ${st}`, 'network');
          const shot = await captureScreenshotBlob();
          const snap = buildSnapshot(entry); snap.page.screenshot = shot;

          await handleSnapshot(snap);
        }
      } catch {}
    });

    return XHR_SEND.apply(this, arguments as any);
  };
}

// ---------- global errors
function installGlobalErrors() {
  window.addEventListener('error', async (e) => {
    const err: any = (e as any).error;
    const info = { message: err?.message || (e as any).message || 'Unhandled error', stack: err?.stack || '', source: 'onerror' };
    addBreadcrumbInternal(info.message, 'error');
    const shot = await captureScreenshotBlob();
    const snap = buildSnapshot(undefined, info); snap.page.screenshot = shot;

    await handleSnapshot(snap);
  });

  window.addEventListener('unhandledrejection', async (e: PromiseRejectionEvent) => {
    const r: any = (e && (e as any).reason);
    const info = { message: r?.message || String(r), stack: r?.stack || '', source: 'unhandledrejection' };
    addBreadcrumbInternal(info.message, 'error');
    const shot = await captureScreenshotBlob();
    const snap = buildSnapshot(undefined, info); snap.page.screenshot = shot;

    await handleSnapshot(snap);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && CFG.triggerMode !== 'manual') {
      void flushOffline();
    }
  });
}

// ---------- offline queue
function queueOffline(meta: any, screenshot: Blob | null) {
  try {
    const q = JSON.parse(localStorage.getItem('tsnap-queue') || '[]');
    const item: any = { ts: Date.now(), meta, transport: CFG.transport };
    if (screenshot) {
      const fr = new FileReader();
      fr.onload = () => { item.screenshotDataUrl = fr.result; q.push(item); localStorage.setItem('tsnap-queue', JSON.stringify(q)); };
      fr.readAsDataURL(screenshot);
    } else { q.push(item); localStorage.setItem('tsnap-queue', JSON.stringify(q)); }
  } catch {}
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [hdr, b64] = dataUrl.split(',');
  const mime = /data:(.*?);base64/.exec(hdr)?.[1] || 'application/octet-stream';
  const bin = atob(b64); const arr = new Uint8Array(bin.length);
  for (let i=0;i<bin.length;i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

async function flushOffline(): Promise<void> {
  try {
    const q: any[] = JSON.parse(localStorage.getItem('tsnap-queue') || '[]');
    if (!q.length) return;
    const next = q.shift();
    const fd = new FormData();

    if (next.transport === 'formFields') {
      const meta = next.meta || {};
      fd.append('ProjectKey', CFG.projectKey);
      fd.append('Title', meta.title || 'Auto-captured error');
      if (meta.summary) fd.append('Summary', meta.summary);
      (meta.labels || []).forEach((l:string)=>fd.append('Labels', l));
      fd.append('SnapshotJson', meta.snapJsonText || '{}');
      if (meta.extraNotes) fd.append('ExtraNotes', meta.extraNotes);
      appendFormFields(fd, meta.linkFields);   // carry the cross-system link across an offline replay
    } else {
      fd.append('metadata', new Blob([JSON.stringify(next.meta)], { type: 'application/json' }), 'metadata.json');
    }

    if (next.screenshotDataUrl) fd.append('Attachments', dataUrlToBlob(next.screenshotDataUrl), `screenshot-${next.ts}.png`);
    const headers = new Headers(); const url = applyAuth(CFG.endpoint, headers);

    // 1s delay before sending
    await sleep(1000);

    await fetch(url, { method: 'POST', body: fd, headers });
    localStorage.setItem('tsnap-queue', JSON.stringify(q));
  } catch {
    // If it fails, keep queue as-is (no destructive changes)
  }
}

export const api = {
  init,
  reportNow(extraNotes?: string, options?: { includeScreenshot?: boolean }) {
    const includeShot = options?.includeScreenshot ?? true;
    const entry = RING[RING.length - 1];
    const capture = includeShot ? captureScreenshotBlob() : Promise.resolve(null);
    capture.then((shot) => {
      const snap = buildSnapshot(entry); snap.page.screenshot = shot;
      if (CFG.promptUser) presentReport(snap, extraNotes || '');
      else void submitSnapshot(snap, extraNotes || '');
    });
  },
  async flushOfflineNow() { await flushOffline(); },

  /** Open the typed cross-system ticket form (fetches the project's categories). */
  openTicketForm(opts?: CreateTicketOptions) { return openTicketFormFlow(opts ?? {}); },
  /** Create a typed ticket programmatically (no UI). Resolves to the ticket number or null. */
  createTicket(opts: CreateTicketOptions) { return createTicket(opts); },
  /** Fetch configured categories/subtypes for the project. */
  getTicketTypes() { return fetchTicketTypes(); },
  getLinkableResources() { return fetchLinkableResources(); },
  /** Start polling for completion notifications and surface them as toasts. */
  enableNotifications(opts?: NotificationsOptions) { enableNotifications(opts ?? {}); },
  /** Stop polling for completion notifications. */
  disableNotifications() { disableNotifications(); },
  /** Update the current operator identity (e.g. after login). */
  setUser(user: TicketUser) { if (typeof CFG !== 'undefined') CFG.user = user; },

  /**
   * Set the cross-system resource the user is currently on (e.g. an Order). Auto-captured errors
   * and `reportNow()` then link the bug-report ticket to it and show it in the Report-a-problem
   * panel. Call on route change into the resource's page; pass status/fields/rawJson when you have
   * them. A custom `resolveLink` config takes precedence over this when provided.
   */
  setActiveLink(link: ResourceLink | null) { ACTIVE_LINK = normalizeLink(link); },
  /** Clear the active resource link (e.g. when leaving the resource's page). */
  clearActiveLink() { ACTIVE_LINK = null; },
  /** Get the active resource link (for debugging or custom UI). */
  getActiveLink(): ResourceLink | null { return ACTIVE_LINK; },
  /** Open the floating help launcher menu programmatically. */
  openLauncher() { LAUNCHER?.open(); },
  /** Close the floating help launcher menu. */
  closeLauncher() { LAUNCHER?.close(); },
  /** Remove the floating launcher from the page. */
  destroyLauncher() { LAUNCHER?.destroy(); LAUNCHER = null; },

  /** Add a breadcrumb (e.g. "Clicked checkout", "Opened modal"). Included in next snapshot. */
  addBreadcrumb(message: string, category?: string) {
    if (typeof CFG !== 'undefined' && CFG.maxBreadcrumbs) addBreadcrumbInternal(message, category);
  },
  /** Set context key-value (e.g. userId, route). Merged into every snapshot. */
  setContext(keyOrObj: string | Record<string, unknown>, value?: unknown) {
    if (typeof keyOrObj === 'string') CONTEXT[keyOrObj] = value;
    else Object.assign(CONTEXT, keyOrObj);
  },
  /** Get current context (for debugging or custom UI). */
  getContext(): Record<string, unknown> {
    return { ...CONTEXT };
  },
  /** Get recent network entries (for debugging or custom UI). */
  getRecentNetwork(): unknown[] {
    return RING.slice();
  },
  /** Get breadcrumb trail (for debugging or custom UI). */
  getBreadcrumbs(): BreadcrumbEntry[] {
    return BREADCRUMBS.slice();
  },

  version: __VERSION__
};

export type { TicketSnapConfig, Snapshot, RedactRule, TriggerMode, BreadcrumbEntry, TicketUser, CreateTicketOptions, NotificationsOptions, TicketCategoryDef, LinkableResourceDef, SnapMode, LauncherConfig, ResourceLink, LinkResolveInfo };
export default {
  init,
  reportNow: api.reportNow,
  flushOfflineNow: api.flushOfflineNow,
  openTicketForm: api.openTicketForm,
  createTicket: api.createTicket,
  getTicketTypes: api.getTicketTypes,
  getLinkableResources: api.getLinkableResources,
  enableNotifications: api.enableNotifications,
  disableNotifications: api.disableNotifications,
  setUser: api.setUser,
  setActiveLink: api.setActiveLink,
  clearActiveLink: api.clearActiveLink,
  getActiveLink: api.getActiveLink,
  openLauncher: api.openLauncher,
  closeLauncher: api.closeLauncher,
  destroyLauncher: api.destroyLauncher,
  addBreadcrumb: api.addBreadcrumb,
  setContext: api.setContext,
  getContext: api.getContext,
  getRecentNetwork: api.getRecentNetwork,
  getBreadcrumbs: api.getBreadcrumbs,
  version: __VERSION__
};
